/* eslint-disable camelcase */
import { z } from 'zod'

import type {
  GetMeetingReportsGroupsGroupByEnum,
  GetMeetingReportsOverviewReportTypeEnum,
  MeetingReportItemSeverityEnum,
} from './generated/api'
import { fetchAPI } from '.'

const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)

export type Severity = MeetingReportItemSeverityEnum
export const severities = ['s0', 's1', 's2', 's3'] as const satisfies readonly Severity[]
const severitySchema = z.enum(severities)

export type ReportType = GetMeetingReportsOverviewReportTypeEnum
export const reportTypes = ['meeting', 'inline'] as const satisfies readonly ReportType[]

export const reportDimensions = [
  'signature',
  'output_device_family',
  'output_device',
  'mic_device',
  'meeting_platform',
  'app_version',
  'known_issue',
  'job_status',
  'report_category',
  'report_type',
  'severity',
] as const satisfies readonly GetMeetingReportsGroupsGroupByEnum[]
export type ReportDimension = GetMeetingReportsGroupsGroupByEnum
export type FacetDimension = Exclude<ReportDimension, 'severity' | 'report_type'>
const dimensionSchema = z.enum(reportDimensions)

const severityCountsSchema = z.object({
  s0: count,
  s1: count,
  s2: count,
  s3: count,
})

export const reportSummarySchema = z.object({
  report_count: count,
  meeting_count: count,
  reporter_count: count,
  signature_count: count,
  weighted: count,
  severity: severityCountsSchema,
})

const overviewSchema = z.object({
  summary: reportSummarySchema,
  previous: reportSummarySchema,
  trend: z.array(z.object({ date: z.string(), severity: severityCountsSchema })),
  // The server only facets a subset of the dimensions, so every key is optional.
  facets: z.record(z.string(), z.array(z.string())) as z.ZodType<Partial<Record<FacetDimension, string[]>>>,
  latest_created_at: z.string().datetime({ offset: true }).nullable(),
})

export const reportGroupSchema = z.object({
  value: z.string(),
  summary: reportSummarySchema,
  previous_report_count: count,
  top_signature: z.string().nullable().default(null),
  s0_signatures: z.array(z.string()).default([]),
})

const groupsSchema = z.object({
  dimension: dimensionSchema,
  groups: z.array(reportGroupSchema),
  truncated: z.boolean(),
})

const cohortsSchema = z.object({
  report_count: count,
  cohorts: z.array(
    z.object({
      dimension: dimensionSchema,
      values: z.array(z.object({ value: z.string(), report_count: count, s0_count: count })),
      other_count: count,
    })
  ),
})

export const meetingReportItemSchema = z.object({
  id: z.guid(),
  meeting_id: z.guid(),
  created_at: z.string().datetime({ offset: true }),
  reported_at: z.string().datetime({ offset: true }),
  reporter_email: z.string().nullable().default(null),
  severity: severitySchema,
  signature: z.string(),
  known_issue: z.string().nullable().default(null),
  report_type: z.string(),
  report_category: z.string(),
  job_status: z.string().nullable().default(null),
  meeting_platform: z.string().nullable().default(null),
  output_device: z.string().nullable().default(null),
  output_device_family: z.string(),
  mic_device: z.string().nullable().default(null),
  app_version: z.string().nullable().default(null),
  feedback: z.string().nullable().default(null),
  rationale: z.string().nullable().default(null),
  slack_channel: z.string().nullable().default(null),
  slack_message_ts: z.string().nullable().default(null),
  viewer_meeting_id: z.guid().nullable().default(null),
})

const reportsSchema = z.object({
  items: z.array(meetingReportItemSchema),
  next_cursor: z.string().nullable(),
})

export type SeverityCounts = z.infer<typeof severityCountsSchema>
export type ReportSummary = z.infer<typeof reportSummarySchema>
export type ReportOverview = z.infer<typeof overviewSchema>
export type ReportGroup = z.infer<typeof reportGroupSchema>
export type ReportGroups = z.infer<typeof groupsSchema>
export type ReportCohorts = z.infer<typeof cohortsSchema>
export type ReportCohort = ReportCohorts['cohorts'][number]
export type MeetingReportItem = z.infer<typeof meetingReportItemSchema>
export type MeetingReportItems = z.infer<typeof reportsSchema>

export type ReportFilters = Partial<Record<FacetDimension, string>> & {
  start_at: string
  end_at: string
  severity?: Severity
  report_type?: ReportType
  wispr_user?: boolean
  search?: string
}

const BASE_PATH = '/api/v1/internal/meeting-reports/observability'
export const REPORTS_PAGE_SIZE = 25

const parseResponse = <T extends z.ZodTypeAny>(schema: T, data: unknown): z.infer<T> => {
  const result = schema.safeParse(data)
  if (result.success) return result.data
  console.warn('Meeting-report response schema mismatch', {
    issues: result.error.issues.map(({ path, code }) => ({
      path: path[0] === 'facets' ? ['facets', '*'] : path,
      code,
    })),
  })
  throw result.error
}

const get = async <T extends z.ZodTypeAny>(
  schema: T,
  path: string,
  params: Record<string, string | number | boolean | undefined>,
  signal?: AbortSignal
) => {
  const response = await fetchAPI<unknown>({
    method: 'GET',
    url: `${BASE_PATH}${path}`,
    params,
    signal,
    expectedStatuses: [403],
  })
  return parseResponse(schema, response.data)
}

export const getReportOverview = (filters: ReportFilters, signal?: AbortSignal) =>
  get(overviewSchema, '', filters, signal)

export const getReportGroups = (filters: ReportFilters, groupBy: ReportDimension, signal?: AbortSignal) =>
  get(groupsSchema, '/groups', { ...filters, group_by: groupBy }, signal)

export const getReportCohorts = (filters: ReportFilters, signal?: AbortSignal) =>
  get(cohortsSchema, '/cohorts', filters, signal)

export const getMeetingReports = (filters: ReportFilters, cursor?: string, signal?: AbortSignal) =>
  get(reportsSchema, '/reports', { ...filters, limit: REPORTS_PAGE_SIZE, ...(cursor ? { cursor } : {}) }, signal)
